import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Card, Button, Space, Switch, Modal, 
  Form, Input, message, Typography, Popconfirm,
  Tag, Select
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined,
  KeyOutlined, ExclamationCircleOutlined, 
  SearchOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';

const { Title } = Typography;

const AccountManagement = () => {
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPwdModalOpen, setIsPwdModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [form] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRolesAccount, setEditingRolesAccount] = useState(null);
  const [roleForm] = Form.useForm();

  // 模拟角色数据
  const ROLE_OPTIONS = [
    { label: '管理员', value: 'admin' },
    { label: '运维人员', value: 'operator' },
    { label: '客服专员', value: 'support' },
  ];

  // 模拟账号数据
  const [accounts, setAccounts] = useState([
    { id: 1, account: 'admin', name: '管理员', status: true, roles: ['admin'], email: 'admin@example.com', phone: '13800138000' },
    { id: 2, account: 'zhangsan', name: '张三', status: true, roles: ['operator'], email: 'zhangsan@example.com', phone: '13912345678' },
    { id: 3, account: 'lisi', name: '李四', status: false, roles: ['support'], email: 'lisi@example.com', phone: '13788889999' },
  ]);

  const [searchValues, setSearchValues] = useState({});

  const displayData = useMemo(() => {
    const { account, name, phone, role } = searchValues;
    return accounts.filter(item => {
      const matchAccount = !account || item.account.includes(account);
      const matchName = !name || item.name.includes(name);
      const matchPhone = !phone || item.phone.includes(phone);
      const matchRole = !role || item.roles.includes(role);
      return matchAccount && matchName && matchPhone && matchRole;
    });
  }, [accounts, searchValues]);

  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam) {
      searchForm.setFieldsValue({ role: roleParam });
      setSearchValues(prev => ({ ...prev, role: roleParam }));
    }
  }, [searchParams, searchForm]);

  const handleSearch = () => {
    setSearchValues(searchForm.getFieldsValue());
  };

  const handleReset = () => {
    searchForm.resetFields();
    setSearchValues({});
  };

  // 处理新增/编辑弹窗
  const showModal = () => {
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const newAccount = {
        id: Date.now(),
        account: values.account,
        name: values.name || values.account,
        phone: values.phone,
        roles: values.roles,
        email: values.email,
        status: true,
      };
      setAccounts([...accounts, newAccount]);
      message.success('账号创建成功');
      setIsModalOpen(false);
    } catch (error) {
      console.log('Validate Failed:', error);
    }
  };

  // 处理修改密码
  const showPwdModal = (record) => {
    setEditingAccount(record);
    pwdForm.resetFields();
    setIsPwdModalOpen(true);
  };

  const handleUpdatePwd = async () => {
    try {
      // 这里模拟调用接口
      message.success(`账号 ${editingAccount.account} 密码修改成功`);
      setIsPwdModalOpen(false);
    } catch (error) {
      console.log('Validate Failed:', error);
    }
  };

  // 删除账号
  const handleDelete = (record) => {
    setAccounts(accounts.filter(item => item.id !== record.id));
    message.success('账号已删除');
  };

  // 角色编辑
  const showRoleModal = (record) => {
    setEditingRolesAccount(record);
    roleForm.setFieldsValue({ roles: record.roles });
    setIsRoleModalOpen(true);
  };

  const handleRoleSave = async () => {
    try {
      const values = await roleForm.validateFields();
      setAccounts(accounts.map(item =>
        item.id === editingRolesAccount.id ? { ...item, roles: values.roles } : item
      ));
      message.success(`账号 ${editingRolesAccount.account} 角色已更新`);
      setIsRoleModalOpen(false);
    } catch (error) {
      console.log('Validate Failed:', error);
    }
  };
  const handleStatusChange = (checked, record) => {
    setAccounts(accounts.map(item => 
      item.id === record.id ? { ...item, status: checked } : item
    ));
    message.success(`账号 ${record.account} 已${checked ? '启用' : '禁用'}`);
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: '登录账号',
      dataIndex: 'account',
      key: 'account',
    },
    {
      title: '用户名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => text || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      render: (text) => text || '-',
    },
    {
      title: '角色',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles, record) => (
        <Space size={[4, 4]} wrap>
          {roles?.map(r => {
            const role = ROLE_OPTIONS.find(opt => opt.value === r);
            return <Tag color="blue" key={r}>{role ? role.label : r}</Tag>;
          })}
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showRoleModal(record)}
            style={{ padding: 0 }}
          />
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (status, record) => (
        <Switch 
          checked={status} 
          onChange={(checked) => handleStatusChange(checked, record)} 
          checkedChildren="启用" 
          unCheckedChildren="禁用" 
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="link" 
            icon={<KeyOutlined />} 
            onClick={() => showPwdModal(record)}
          >
            修改密码
          </Button>
          <Popconfirm
            title="风险提示"
            description={`确定要删除账号 "${record.account}" 吗？此操作不可恢复！`}
            onConfirm={() => handleDelete(record)}
            okText="确定删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>内部账号</Title>
      </div>
      <Card 
        bordered={false}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
            创建账号
          </Button>
        }
      >
        <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
          <Form form={searchForm} layout="inline" onFinish={handleSearch}>
            <Form.Item name="account" >
              <Input placeholder="输入登录账号" allowClear />
            </Form.Item>
            <Form.Item name="name">
              <Input placeholder="输入用户名" allowClear />
            </Form.Item>
            <Form.Item name="phone">
              <Input placeholder="输入手机号" allowClear />
            </Form.Item>
            <Form.Item name="role">
              <Select placeholder="选择角色" allowClear style={{ width: 150 }} options={ROLE_OPTIONS} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} htmlType="submit">
                  查询
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>

        <Table 
          columns={columns} 
          dataSource={displayData} 
          rowKey="id"
          pagination={{
            showTotal: (total) => `共 ${total} 条记录`,
            showSizeChanger: true,
            defaultPageSize: 10,
          }}
        />
      </Card>

      {/* 创建账号弹窗 */}
      <Modal
        title="创建账号"
        open={isModalOpen}
        onOk={handleCreate}
        onCancel={() => setIsModalOpen(false)}
        destroyOnClose
        okText="确认创建"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="account"
            label="登录账号"
            rules={[
              { required: true, message: '请输入登录账号' },
              { min: 6, max: 18, message: '长度需在6-18位之间' },
              { pattern: /^[^\u4e00-\u9fa5]+$/, message: '账号不能包含中文' }
            ]}
          >
            <Input placeholder="6-18位，非中文" />
          </Form.Item>
          <Form.Item
            name="password"
            label="登录密码"
            rules={[{ required: true, message: '请输入登录密码' }]}
          >
            <Input.Password placeholder="请输入登录密码" />
          </Form.Item>
          <Form.Item
            name="name"
            label="用户名"
            extra="不填则默认使用登录账号"
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^\d+$/, message: '请输入有效的手机号' }
            ]}
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item
            name="roles"
            label="关联角色"
            rules={[{ required: true, message: '请至少选择一个角色' }]}
          >
            <Select 
              mode="multiple" 
              placeholder="请选择角色" 
              options={ROLE_OPTIONS} 
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改密码弹窗 */}
      <Modal
        title={`修改密码 - ${editingAccount?.account}`}
        open={isPwdModalOpen}
        onOk={handleUpdatePwd}
        onCancel={() => setIsPwdModalOpen(false)}
        destroyOnClose
        okText="提交"
        cancelText="取消"
      >
        <Form form={pwdForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[{ required: true, message: '请输入新密码' }]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改角色弹窗 */}
      <Modal
        title={`修改角色 - ${editingRolesAccount?.account}`}
        open={isRoleModalOpen}
        onOk={handleRoleSave}
        onCancel={() => setIsRoleModalOpen(false)}
        destroyOnClose
        okText="保存"
        cancelText="取消"
      >
        <Form form={roleForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="roles"
            label="关联角色"
            rules={[{ required: true, message: '请至少选择一个角色' }]}
          >
            <Select
              mode="multiple"
              placeholder="请选择角色"
              options={ROLE_OPTIONS}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountManagement;
